import React from 'react';
import { SentenceApi } from '../api/SentenceApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import { editablePinyin } from '../component/studentInstructionalPinyin';

interface UsePinyinFieldProps {
    history: any;
    location: any;
    setErrorMessage: (msg: string) => void;
    initialText?: string;
}

export function useSentenceChinesePinyin({
    history,
    location,
    setErrorMessage,
    initialText = '',
}: UsePinyinFieldProps) {
    const [text, setText] = React.useState(initialText);
    const [pinyin, setPinyin] = React.useState<string>('');
    const [pinyinList, setPinyinList] = React.useState<string[]>([]);
    const [pinyinErrorText, setPinyinErrorText] = React.useState<string | null>(null);
    const fetchRequestIdRef = React.useRef(0);
    const validationRequestIdRef = React.useRef(0);

    const setPinyinFields = React.useCallback((nextPinyinList: string[]) => {
        setPinyin(nextPinyinList.join(' '));
        setPinyinList(nextPinyinList);
    }, []);

    const validatePinyin = React.useCallback((text: string, pinyinList: string[], requestId: number) => {
        SentenceApi.isTextPinyinValid(text, pinyinList)
            .then((response) => {
                if (requestId !== validationRequestIdRef.current) return;
                const isValid = response.data;
                if (isValid) {
                    setPinyinErrorText(null);
                } else {
                    setPinyinErrorText('Pinyin invalid or incorrect length');
                }
            })
            .catch((err) => {
                if (requestId !== validationRequestIdRef.current) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                setPinyinErrorText('Unable to validate pinyin');
            });
    }, [setErrorMessage, history, location]);

    const fetchNumericPinyinForText = React.useCallback((value: string) => {
        const requestId = ++fetchRequestIdRef.current;
        validationRequestIdRef.current += 1;

        if (!value.trim()) {
            setPinyinFields([]);
            setPinyinErrorText(null);
            return;
        }

        setPinyin('');
        setPinyinList([]);
        setPinyinErrorText(null);

        SentenceApi.getTextPinyin(value)
            .then((response) => {
                if (requestId !== fetchRequestIdRef.current) return;
                const pinyinList = response.data.flat();
                setPinyinFields(pinyinList);
                setPinyinErrorText(null);
            })
            .catch((err) => {
                if (requestId !== fetchRequestIdRef.current) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                setPinyinErrorText('Unable to fetch pinyin');
            });
    }, [setPinyinFields, setErrorMessage, history, location]);

    const handleTextChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setText(value);
        fetchNumericPinyinForText(value);
    }, [fetchNumericPinyinForText]);

    const handlePinyinChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const pinyinList = value.trim().split(/\s+/).filter(Boolean);
        fetchRequestIdRef.current += 1;
        const requestId = ++validationRequestIdRef.current;
        setPinyin(value);
        setPinyinList(pinyinList);
        validatePinyin(text, pinyinList, requestId);
    }, [validatePinyin, text]);

    const setDialogPromptFields = React.useCallback((text: string | null, savedPinyinList: string[] | null, _sentenceId: number | null) => {
        const nextText = text ? text : "";
        setText(nextText)
        if (nextText.trim() && savedPinyinList?.length) {
            fetchRequestIdRef.current += 1;
            validationRequestIdRef.current += 1;
            setPinyinFields(editablePinyin(savedPinyinList).split(' '));
            setPinyinErrorText(null);
        } else {
            fetchNumericPinyinForText(nextText);
        }
    }, [fetchNumericPinyinForText, setPinyinFields]);

    const clearDialogPromptFields = React.useCallback(() => {
        setText(initialText)
        fetchNumericPinyinForText(initialText)
    }, [initialText, fetchNumericPinyinForText]);

    const clearErrorText = React.useCallback(() => {
        setPinyinErrorText(null)
    }, []);

    return {
        text,
        pinyin,
        pinyinList,
        pinyinErrorText,
        setDialogPromptFields,
        clearDialogPromptFields,
        clearErrorText,
        handleTextChange,
        handlePinyinChange,
    };
}
